import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getApiUrl } from '../config/api';
import type { Brand, PageSetting, MarqueeConfig, FAQ, Category, BrandAssetType } from '../types';

const CATEGORY_LABELS: Record<Category, string> = {
  'print': 'Print',
};

const CATEGORY_ORDER: Category[] = ['print'];

type TabType = 'brand-info' | 'page-settings' | 'marquee' | 'faqs';

export function BrandSettingsPage() {
  const { brandId: paramBrandId } = useParams<{ brandId?: string }>();
  const [brands, setBrands] = useState<Brand[]>([]);
  const [selectedBrand, setSelectedBrand] = useState<string>('');
  const [activeTab, setActiveTab] = useState<TabType>('brand-info');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBrands();
  }, []);

  // Sync selected brand when URL param changes (sidebar navigation between brands)
  useEffect(() => {
    if (paramBrandId) {
      setSelectedBrand(paramBrandId);
    }
  }, [paramBrandId]);

  const fetchBrands = async () => {
    try {
      const response = await fetch(getApiUrl('/api/brands'), {
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setBrands(data.brands || []);
        if (data.brands && data.brands.length > 0) {
          setSelectedBrand(paramBrandId ?? data.brands[0].id);
        }
      }
    } catch (error) {
      console.error('Failed to fetch brands:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-dark-text-secondary">Loading brands...</p>
      </div>
    );
  }

  const tabs = [
    { id: 'brand-info' as TabType, name: 'Brand Info', icon: '🏢' },
    { id: 'page-settings' as TabType, name: 'Page Settings', icon: '📄' },
    { id: 'marquee' as TabType, name: 'Marquee Banner', icon: '📰' },
    { id: 'faqs' as TabType, name: 'FAQs', icon: '❓' },
  ];

  return (
    <div>
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-white mb-1 sm:mb-2">Brand Settings</h1>
        <p className="text-dark-text-secondary text-sm sm:text-base">
          Configure page settings, marquee banners, and FAQs for each frontend
        </p>
      </div>

      {/* Brand Selector — only on legacy /brand-settings route (no URL param) */}
      {!paramBrandId && (
        <div className="mb-6">
          <label className="block text-sm font-medium text-dark-text mb-2">
            Select Brand / Frontend
          </label>
          <select
            value={selectedBrand}
            onChange={(e) => setSelectedBrand(e.target.value)}
            className="input w-full md:w-auto"
          >
            {CATEGORY_ORDER.map((category) => {
              const categoryBrands = brands.filter(b => (b.category || 'print') === category);
              if (categoryBrands.length === 0) return null;
              return (
                <optgroup key={category} label={CATEGORY_LABELS[category]}>
                  {categoryBrands.map((brand) => (
                    <option key={brand.id} value={brand.id}>
                      {brand.name} ({brand.domain})
                    </option>
                  ))}
                </optgroup>
              );
            })}
          </select>
        </div>
      )}

      {/* Brand title pill — when accessed via /brands/:brandId */}
      {paramBrandId && selectedBrand && (
        <div className="mb-6 flex items-center gap-3 p-3 bg-dark-surface rounded-lg border border-dark-border">
          <div className="w-2 h-8 bg-blue-600 rounded-full" />
          <div>
            <p className="text-xs text-dark-text-secondary uppercase tracking-wider">Editing brand</p>
            <p className="text-base font-semibold text-white">
              {brands.find(b => b.id === selectedBrand)?.name ?? selectedBrand}
            </p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-dark-border mb-6 -mx-4 sm:mx-0">
        <div className="flex gap-1 sm:gap-2 overflow-x-auto px-4 sm:px-0">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-3 sm:px-6 py-3 font-medium transition-colors border-b-2 whitespace-nowrap text-sm sm:text-base ${
                activeTab === tab.id
                  ? 'border-blue-500 text-white'
                  : 'border-transparent text-dark-text-secondary hover:text-dark-text'
              }`}
            >
              <span className="mr-1 sm:mr-2">{tab.icon}</span>
              {tab.name}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      {selectedBrand && (
        <>
          {activeTab === 'brand-info' && <BrandInfoTab brandId={selectedBrand} brands={brands} refetch={fetchBrands} />}
          {activeTab === 'page-settings' && <PageSettingsTab brandId={selectedBrand} />}
          {activeTab === 'marquee' && <MarqueeTab brandId={selectedBrand} />}
          {activeTab === 'faqs' && <FAQsTab brandId={selectedBrand} />}
        </>
      )}
    </div>
  );
}

// ============================================
// BRAND INFO TAB
// ============================================
function BrandInfoTab({ brandId, brands, refetch }: { brandId: string; brands: Brand[]; refetch: () => void }) {
  const brand = brands.find(b => b.id === brandId);
  const [formData, setFormData] = useState({
    name: '',
    domain: '',
    vertical: 'copiers' as string,
    category: 'print' as string,
    phone: '',
    email: '',
    whatsapp: '',
    company_name: '',
    company_number: '',
    address: '',
    logo_url: '',
    favicon_url: '',
    apple_touch_icon_url: '',
    og_image_url: '',
    build_hook_url: '',
    google_analytics_id: '',
  });
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (brand) {
      setFormData({
        name: brand.name || '',
        domain: brand.domain || '',
        vertical: brand.vertical || 'copiers',
        category: brand.category || 'print',
        phone: brand.phone || '',
        email: brand.email || '',
        whatsapp: brand.whatsapp || '',
        company_name: brand.company_name || '',
        company_number: brand.company_number || '',
        address: brand.address || '',
        logo_url: brand.logo_url || '',
        favicon_url: brand.favicon_url || '',
        apple_touch_icon_url: brand.apple_touch_icon_url || '',
        og_image_url: brand.og_image_url || '',
        build_hook_url: brand.build_hook_url || '',
        google_analytics_id: brand.google_analytics_id || '',
      });
    }
  }, [brand]);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(getApiUrl(`/api/brands/${brandId}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setSuccess('Brand information updated successfully');
        refetch();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to update brand');
      }
    } catch (error) {
      setError('Failed to update brand');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Success/Error Messages */}
      {success && (
        <div className="p-4 bg-green-900/20 border border-green-500 rounded-lg text-green-400">
          {success}
        </div>
      )}
      {error && (
        <div className="p-4 bg-red-900/20 border border-red-500 rounded-lg text-red-400">
          {error}
        </div>
      )}

      {/* Brand Information */}
      <div className="card p-6">
        <h2 className="text-xl font-bold text-white mb-6">Brand Information</h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-dark-text mb-2">
              Brand Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="input w-full"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-dark-text mb-2">
              Domain *
            </label>
            <input
              type="text"
              value={formData.domain}
              onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
              className="input w-full"
              placeholder="example.co.uk"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-dark-text mb-2">
              Category
            </label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              className="input w-full"
            >
              <option value="print">Print</option>
            </select>
            <p className="text-xs text-dark-text-secondary mt-1">
              Groups this brand under a category in the admin panel
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-dark-text mb-2">
              Vertical
            </label>
            <select
              value={formData.vertical}
              onChange={(e) => setFormData({ ...formData, vertical: e.target.value })}
              className="input w-full"
            >
              <option value="copiers">Copiers</option>
            </select>
            <p className="text-xs text-dark-text-secondary mt-1">
              Determines which calculator and pricing models are used
            </p>
          </div>
        </div>
      </div>

      {/* Contact Information */}
      <div className="card p-6">
        <h2 className="text-xl font-bold text-white mb-6">Contact Information</h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-dark-text mb-2">
              Phone Number
            </label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="input w-full"
              placeholder="0843 5576667"
            />
            <p className="text-xs text-dark-text-secondary mt-1">
              Displayed on website header and footer
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-dark-text mb-2">
              Email Address
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="input w-full"
              placeholder="hello@example.co.uk"
            />
            <p className="text-xs text-dark-text-secondary mt-1">
              Displayed on website for contact
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-dark-text mb-2">
              WhatsApp Number
            </label>
            <input
              type="tel"
              value={formData.whatsapp}
              onChange={(e) => setFormData({ ...formData, whatsapp: e.target.value })}
              className="input w-full"
              placeholder="448435576667"
            />
            <p className="text-xs text-dark-text-secondary mt-1">
              Format: country code + number (no +, spaces, or dashes)
            </p>
          </div>
        </div>
      </div>

      {/* Company Details */}
      <div className="card p-6">
        <h2 className="text-xl font-bold text-white mb-6">Company Details</h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-dark-text mb-2">
              Company Name
            </label>
            <input
              type="text"
              value={formData.company_name}
              onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
              className="input w-full"
              placeholder="Britannia IT Ltd"
            />
            <p className="text-xs text-dark-text-secondary mt-1">
              Displayed in footer
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-dark-text mb-2">
              Company Number
            </label>
            <input
              type="text"
              value={formData.company_number}
              onChange={(e) => setFormData({ ...formData, company_number: e.target.value })}
              className="input w-full"
              placeholder="16562719"
            />
            <p className="text-xs text-dark-text-secondary mt-1">
              UK Companies House registration number
            </p>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-dark-text mb-2">
              Address
            </label>
            <textarea
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              className="input w-full min-h-[80px]"
              placeholder="123 High Street, London, UK"
            />
            <p className="text-xs text-dark-text-secondary mt-1">
              Company registered address
            </p>
          </div>
        </div>
      </div>

      {/* Brand Assets */}
      <div className="card p-6">
        <h2 className="text-xl font-bold text-white mb-2">Brand Assets</h2>
        <p className="text-sm text-dark-text-secondary mb-6">
          Upload favicon, social-share image, and logo. Each frontend pulls these in at build time;
          set a build hook below to auto-redeploy whenever you upload.
        </p>

        <div className="space-y-6">
          <BrandAssetUploader
            brandId={brandId}
            assetType="favicon"
            label="Favicon"
            hint="32×32 or 64×64 PNG / ICO. Shown in browser tabs and search results."
            currentUrl={formData.favicon_url}
            onUploaded={(url) => {
              setFormData(d => ({ ...d, favicon_url: url }));
              refetch();
            }}
            onRemoved={() => {
              setFormData(d => ({ ...d, favicon_url: '' }));
              refetch();
            }}
          />

          <BrandAssetUploader
            brandId={brandId}
            assetType="apple_touch_icon"
            label="Apple Touch Icon"
            hint="180×180 PNG. Shown when iOS users add the site to their home screen."
            currentUrl={formData.apple_touch_icon_url}
            onUploaded={(url) => {
              setFormData(d => ({ ...d, apple_touch_icon_url: url }));
              refetch();
            }}
            onRemoved={() => {
              setFormData(d => ({ ...d, apple_touch_icon_url: '' }));
              refetch();
            }}
          />

          <BrandAssetUploader
            brandId={brandId}
            assetType="og_image"
            label="Social Share Image (Open Graph)"
            hint="1200×630 PNG / JPG. Shown when the site is shared on LinkedIn, Slack, WhatsApp, X, etc."
            currentUrl={formData.og_image_url}
            onUploaded={(url) => {
              setFormData(d => ({ ...d, og_image_url: url }));
              refetch();
            }}
            onRemoved={() => {
              setFormData(d => ({ ...d, og_image_url: '' }));
              refetch();
            }}
          />

          <BrandAssetUploader
            brandId={brandId}
            assetType="logo"
            label="Logo"
            hint="Square SVG preferred (used in Organization schema and the site header)."
            currentUrl={formData.logo_url}
            onUploaded={(url) => {
              setFormData(d => ({ ...d, logo_url: url }));
              refetch();
            }}
            onRemoved={() => {
              setFormData(d => ({ ...d, logo_url: '' }));
              refetch();
            }}
          />
        </div>
      </div>

      {/* Build Hook */}
      <div className="card p-6">
        <h2 className="text-xl font-bold text-white mb-2">Build Hook</h2>
        <p className="text-sm text-dark-text-secondary mb-4">
          Optional Cloudflare Pages build webhook URL. When set, every successful asset upload
          fires a POST to this URL so the static site rebuilds automatically and picks up the
          new assets within a few minutes.
        </p>
        <input
          type="url"
          value={formData.build_hook_url}
          onChange={(e) => setFormData({ ...formData, build_hook_url: e.target.value })}
          className="input w-full"
          placeholder="https://api.cloudflare.com/client/v4/pages/webhooks/deploy_hooks/..."
        />
        <p className="text-xs text-dark-text-secondary mt-2">
          Find this under Cloudflare Pages → your project → Settings → Builds &amp; deployments → Deploy hooks.
          Save the brand to persist this value before uploading assets.
        </p>
      </div>

      {/* Google Analytics */}
      <div className="card p-6">
        <h2 className="text-xl font-bold text-white mb-6">Google Analytics</h2>
        <div>
          <label className="block text-sm font-medium text-dark-text mb-2">
            Measurement ID
          </label>
          <input
            type="text"
            value={formData.google_analytics_id}
            onChange={(e) => setFormData({ ...formData, google_analytics_id: e.target.value })}
            className="input w-full"
            placeholder="G-XXXXXXXXXX"
          />
          <p className="text-xs text-dark-text-secondary mt-1">
            Google Analytics 4 measurement ID (starts with G-). Leave empty to disable tracking.
          </p>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="btn btn-primary px-8"
        >
          {saving ? 'Saving...' : 'Save Brand Information'}
        </button>
      </div>
    </div>
  );
}

// ============================================
// BRAND ASSET UPLOADER
// ============================================
interface BrandAssetUploaderProps {
  brandId: string;
  assetType: BrandAssetType;
  label: string;
  hint: string;
  currentUrl: string;
  onUploaded: (url: string) => void;
  onRemoved: () => void;
}

function BrandAssetUploader({
  brandId,
  assetType,
  label,
  hint,
  currentUrl,
  onUploaded,
  onRemoved,
}: BrandAssetUploaderProps) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const handleUpload = async (file: File) => {
    setError('');
    setInfo('');
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);

      const res = await fetch(getApiUrl(`/api/brands/${brandId}/assets/${assetType}`), {
        method: 'POST',
        credentials: 'include',
        body: form,
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || `Upload failed (${res.status})`);
        return;
      }

      onUploaded(data.url);
      setInfo(data.buildTriggered
        ? 'Uploaded — build hook fired, site will redeploy in a few minutes.'
        : 'Uploaded. Set a build hook below to auto-redeploy on the next upload.');
      setTimeout(() => setInfo(''), 6000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = async () => {
    if (!confirm(`Remove the current ${label.toLowerCase()}?`)) return;
    setError('');
    setInfo('');
    setUploading(true);
    try {
      const res = await fetch(getApiUrl(`/api/brands/${brandId}/assets/${assetType}`), {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.error || `Delete failed (${res.status})`);
        return;
      }
      onRemoved();
      setInfo('Removed.');
      setTimeout(() => setInfo(''), 4000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="border border-dark-border rounded-lg p-4">
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="flex-1">
          <div className="text-sm font-semibold text-white">{label}</div>
          <div className="text-xs text-dark-text-secondary mt-0.5">{hint}</div>
        </div>
        {currentUrl && (
          <div className="flex-shrink-0 w-16 h-16 bg-dark-bg rounded-md flex items-center justify-center overflow-hidden border border-dark-border">
            <img
              src={currentUrl}
              alt={`${label} preview`}
              className="max-w-full max-h-full object-contain"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <label className="btn btn-secondary cursor-pointer">
          {uploading ? 'Working…' : currentUrl ? 'Replace' : 'Upload'}
          <input
            type="file"
            accept="image/png,image/jpeg,image/webp,image/svg+xml,image/x-icon,image/vnd.microsoft.icon"
            className="hidden"
            disabled={uploading}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleUpload(f);
              e.currentTarget.value = '';
            }}
          />
        </label>
        {currentUrl && (
          <button
            type="button"
            className="btn btn-ghost text-red-400 hover:text-red-300"
            onClick={handleRemove}
            disabled={uploading}
          >
            Remove
          </button>
        )}
        {currentUrl && (
          <a
            href={currentUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-dark-text-secondary underline truncate max-w-xs"
          >
            {currentUrl}
          </a>
        )}
      </div>

      {error && (
        <p className="mt-2 text-xs text-red-400">{error}</p>
      )}
      {info && (
        <p className="mt-2 text-xs text-green-400">{info}</p>
      )}
    </div>
  );
}

// ============================================
// PAGE SETTINGS TAB
// ============================================
function PageSettingsTab({ brandId }: { brandId: string }) {
  const [settings, setSettings] = useState<PageSetting[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [newSetting, setNewSetting] = useState({
    page: 'homepage',
    setting_key: '',
    setting_value: '',
    setting_type: 'string' as 'string' | 'number' | 'boolean' | 'json',
    description: '',
  });

  useEffect(() => {
    fetchSettings();
  }, [brandId]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        getApiUrl(`/api/page-settings/admin?brand=${brandId}`),
        { credentials: 'include' }
      );

      if (response.ok) {
        const data = await response.json();
        setSettings(data.settings || []);
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddSetting = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      const response = await fetch(getApiUrl('/api/page-settings'), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          brandId,
          ...newSetting,
        }),
      });

      if (response.ok) {
        setSuccess('Setting added successfully');
        setShowAddModal(false);
        setNewSetting({
          page: 'homepage',
          setting_key: '',
          setting_value: '',
          setting_type: 'string',
          description: '',
        });
        fetchSettings();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to add setting');
      }
    } catch (error) {
      setError('Failed to add setting');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateSetting = async (setting: PageSetting, value: string) => {
    try {
      const response = await fetch(getApiUrl('/api/page-settings'), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          brandId: setting.brand_id,
          page: setting.page,
          settingKey: setting.setting_key,
          settingValue: value,
          settingType: setting.setting_type,
          description: setting.description,
        }),
      });

      if (response.ok) {
        fetchSettings();
      }
    } catch (error) {
      console.error('Failed to update setting:', error);
    }
  };

  const handleDeleteSetting = async (id: number) => {
    if (!confirm('Are you sure you want to delete this setting?')) return;

    try {
      const response = await fetch(getApiUrl(`/api/page-settings/${id}`), {
        method: 'DELETE',
        credentials: 'include',
      });

      if (response.ok) {
        setSuccess('Setting deleted');
        fetchSettings();
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (error) {
      setError('Failed to delete setting');
    }
  };

  if (loading) {
    return <div className="text-dark-text-secondary">Loading settings...</div>;
  }

  // Group settings by page
  const settingsByPage = settings.reduce((acc, setting) => {
    if (!acc[setting.page]) acc[setting.page] = [];
    acc[setting.page].push(setting);
    return acc;
  }, {} as Record<string, PageSetting[]>);

  return (
    <div className="space-y-6">
      {/* Success/Error Messages */}
      {success && (
        <div className="p-4 bg-green-900/20 border border-green-500 rounded-lg text-green-400">
          {success}
        </div>
      )}
      {error && (
        <div className="p-4 bg-red-900/20 border border-red-500 rounded-lg text-red-400">
          {error}
        </div>
      )}

      {/* Add Setting Button */}
      <div className="flex justify-end">
        <button
          onClick={() => setShowAddModal(true)}
          className="btn btn-primary"
        >
          + Add New Setting
        </button>
      </div>

      {/* Settings by Page */}
      {Object.keys(settingsByPage).length === 0 ? (
        <div className="card p-8 text-center">
          <p className="text-dark-text-secondary">
            No page settings found. Add your first setting to get started.
          </p>
        </div>
      ) : (
        Object.entries(settingsByPage).map(([page, pageSettings]) => (
          <div key={page} className="card p-6">
            <h2 className="text-xl font-bold text-white mb-4 capitalize">
              {page} Settings
            </h2>
            <div className="space-y-4">
              {pageSettings.map((setting) => (
                <div
                  key={setting.id}
                  className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4 p-4 bg-slate-800 rounded-lg"
                >
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-dark-text mb-2">
                      {setting.setting_key}
                      <span className="ml-2 text-xs text-dark-text-secondary">
                        ({setting.setting_type})
                      </span>
                    </label>
                    <input
                      type="text"
                      value={setting.setting_value}
                      onChange={(e) => handleUpdateSetting(setting, e.target.value)}
                      onBlur={() => {}}
                      className="input w-full"
                      placeholder={`Enter ${setting.setting_key}`}
                    />
                    {setting.description && (
                      <p className="text-xs text-dark-text-secondary mt-1">
                        {setting.description}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => handleDeleteSetting(setting.id)}
                    className="btn btn-secondary text-red-400 hover:bg-red-900/20"
                    title="Delete setting"
                  >
                    🗑️
                  </button>
                </div>
              ))}
            </div>
          </div>
        ))
      )}

      {/* Add Setting Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-start sm:items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="card p-4 sm:p-6 w-full max-w-md my-2 sm:my-0">
            <h3 className="text-xl font-bold text-white mb-4">Add New Setting</h3>
            <form onSubmit={handleAddSetting} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-dark-text mb-2">
                  Page
                </label>
                <select
                  value={newSetting.page}
                  onChange={(e) => setNewSetting({ ...newSetting, page: e.target.value })}
                  className="input w-full"
                  required
                >
                  <option value="homepage">Homepage</option>
                  <option value="calculator">Calculator</option>
                  <option value="faq">FAQ</option>
                  <option value="about">About</option>
                  <option value="contact">Contact</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-dark-text mb-2">
                  Setting Key
                </label>
                <input
                  type="text"
                  value={newSetting.setting_key}
                  onChange={(e) => setNewSetting({ ...newSetting, setting_key: e.target.value })}
                  className="input w-full"
                  placeholder="e.g., google_analytics_id"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-dark-text mb-2">
                  Setting Value
                </label>
                <input
                  type="text"
                  value={newSetting.setting_value}
                  onChange={(e) => setNewSetting({ ...newSetting, setting_value: e.target.value })}
                  className="input w-full"
                  placeholder="Enter value"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-dark-text mb-2">
                  Type
                </label>
                <select
                  value={newSetting.setting_type}
                  onChange={(e) =>
                    setNewSetting({
                      ...newSetting,
                      setting_type: e.target.value as 'string' | 'number' | 'boolean' | 'json',
                    })
                  }
                  className="input w-full"
                >
                  <option value="string">String</option>
                  <option value="number">Number</option>
                  <option value="boolean">Boolean</option>
                  <option value="json">JSON</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-dark-text mb-2">
                  Description (Optional)
                </label>
                <input
                  type="text"
                  value={newSetting.description}
                  onChange={(e) => setNewSetting({ ...newSetting, description: e.target.value })}
                  className="input w-full"
                  placeholder="What is this setting for?"
                />
              </div>

              <div className="flex gap-3">
                <button type="submit" disabled={saving} className="btn btn-primary flex-1">
                  {saving ? 'Adding...' : 'Add Setting'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================
// MARQUEE TAB
// ============================================
function MarqueeTab({ brandId }: { brandId: string }) {
  const [config, setConfig] = useState<Partial<MarqueeConfig>>({
    text: '',
    background_color: '#0df20d',
    text_color: '#000000',
    border_color: '#000000',
    speed: 20,
    active: 1,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    fetchMarquee();
  }, [brandId]);

  const fetchMarquee = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        getApiUrl(`/api/marquee/admin?brand=${brandId}`),
        { credentials: 'include' }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.marquees && data.marquees.length > 0) {
          setConfig(data.marquees[0]);
        }
      }
    } catch (error) {
      console.error('Failed to fetch marquee:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(getApiUrl('/api/marquee'), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          brandId,
          text: config.text,
          backgroundColor: config.background_color,
          textColor: config.text_color,
          borderColor: config.border_color,
          speed: config.speed,
          active: config.active,
        }),
      });

      if (response.ok) {
        setSuccess('Marquee configuration saved successfully');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to save marquee');
      }
    } catch (error) {
      setError('Failed to save marquee');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-dark-text-secondary">Loading marquee configuration...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Success/Error Messages */}
      {success && (
        <div className="p-4 bg-green-900/20 border border-green-500 rounded-lg text-green-400">
          {success}
        </div>
      )}
      {error && (
        <div className="p-4 bg-red-900/20 border border-red-500 rounded-lg text-red-400">
          {error}
        </div>
      )}

      {/* Marquee Preview */}
      {config.active === 1 && config.text && (
        <div className="card p-6">
          <h3 className="text-lg font-bold text-white mb-4">Preview</h3>
          <div
            className="overflow-hidden rounded-lg border-4 py-2"
            style={{
              backgroundColor: config.background_color,
              borderColor: config.border_color,
            }}
          >
            <div className="whitespace-nowrap">
              <span
                className="text-2xl font-black italic tracking-tighter uppercase px-4"
                style={{ color: config.text_color }}
              >
                {config.text}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Configuration Form */}
      <div className="card p-6">
        <h3 className="text-xl font-bold text-white mb-4">Marquee Configuration</h3>
        <div className="space-y-4">
          {/* Marquee Text */}
          <div>
            <label className="block text-sm font-medium text-dark-text mb-2">
              Marquee Text
            </label>
            <textarea
              value={config.text}
              onChange={(e) => setConfig({ ...config, text: e.target.value })}
              className="input w-full min-h-[100px]"
              placeholder="Enter scrolling text..."
            />
            <p className="text-xs text-dark-text-secondary mt-1">
              This text will scroll across the top of the page
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            {/* Background Color */}
            <div>
              <label className="block text-sm font-medium text-dark-text mb-2">
                Background Color
              </label>
              <div className="flex gap-2">
                <input
                  type="color"
                  value={config.background_color}
                  onChange={(e) => setConfig({ ...config, background_color: e.target.value })}
                  className="w-12 h-10 rounded cursor-pointer"
                />
                <input
                  type="text"
                  value={config.background_color}
                  onChange={(e) => setConfig({ ...config, background_color: e.target.value })}
                  className="input flex-1"
                  placeholder="#0df20d"
                />
              </div>
            </div>

            {/* Text Color */}
            <div>
              <label className="block text-sm font-medium text-dark-text mb-2">
                Text Color
              </label>
              <div className="flex gap-2">
                <input
                  type="color"
                  value={config.text_color}
                  onChange={(e) => setConfig({ ...config, text_color: e.target.value })}
                  className="w-12 h-10 rounded cursor-pointer"
                />
                <input
                  type="text"
                  value={config.text_color}
                  onChange={(e) => setConfig({ ...config, text_color: e.target.value })}
                  className="input flex-1"
                  placeholder="#000000"
                />
              </div>
            </div>

            {/* Border Color */}
            <div>
              <label className="block text-sm font-medium text-dark-text mb-2">
                Border Color
              </label>
              <div className="flex gap-2">
                <input
                  type="color"
                  value={config.border_color}
                  onChange={(e) => setConfig({ ...config, border_color: e.target.value })}
                  className="w-12 h-10 rounded cursor-pointer"
                />
                <input
                  type="text"
                  value={config.border_color}
                  onChange={(e) => setConfig({ ...config, border_color: e.target.value })}
                  className="input flex-1"
                  placeholder="#000000"
                />
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {/* Speed */}
            <div>
              <label className="block text-sm font-medium text-dark-text mb-2">
                Animation Speed (seconds)
              </label>
              <input
                type="number"
                value={config.speed}
                onChange={(e) => setConfig({ ...config, speed: parseInt(e.target.value) })}
                className="input w-full"
                min="5"
                max="60"
              />
              <p className="text-xs text-dark-text-secondary mt-1">
                How long it takes for text to scroll across (5-60 seconds)
              </p>
            </div>

            {/* Active Toggle */}
            <div>
              <label className="block text-sm font-medium text-dark-text mb-2">
                Display Status
              </label>
              <select
                value={config.active}
                onChange={(e) => setConfig({ ...config, active: parseInt(e.target.value) })}
                className="input w-full"
              >
                <option value={1}>Active (Show marquee)</option>
                <option value={0}>Inactive (Hide marquee)</option>
              </select>
              <p className="text-xs text-dark-text-secondary mt-1">
                Toggle visibility of the marquee banner
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="btn btn-primary px-8"
        >
          {saving ? 'Saving...' : 'Save Marquee Configuration'}
        </button>
      </div>
    </div>
  );
}

// ============================================
// FAQS TAB
// ============================================
function FAQsTab({ brandId }: { brandId: string }) {
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingFaq, setEditingFaq] = useState<FAQ | null>(null);
  const [formData, setFormData] = useState({
    question: '',
    answer: '',
    category: 'general',
    page: 'homepage',
    sort_order: 1,
    active: 1,
  });

  useEffect(() => {
    fetchFAQs();
  }, [brandId]);

  const fetchFAQs = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        getApiUrl(`/api/faqs/admin?brand=${brandId}`),
        { credentials: 'include' }
      );

      if (response.ok) {
        const data = await response.json();
        setFaqs(data.faqs || []);
      }
    } catch (error) {
      console.error('Failed to fetch FAQs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (faq?: FAQ) => {
    if (faq) {
      setEditingFaq(faq);
      setFormData({
        question: faq.question,
        answer: faq.answer,
        category: faq.category || 'general',
        page: faq.page,
        sort_order: faq.sort_order,
        active: faq.active,
      });
    } else {
      setEditingFaq(null);
      setFormData({
        question: '',
        answer: '',
        category: 'general',
        page: 'homepage',
        sort_order: faqs.length + 1,
        active: 1,
      });
    }
    setShowModal(true);
  };

  const handleSaveFAQ = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      const url = editingFaq
        ? getApiUrl(`/api/faqs/${editingFaq.id}`)
        : getApiUrl('/api/faqs');

      const response = await fetch(url, {
        method: editingFaq ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': brandId,
        },
        credentials: 'include',
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setSuccess(editingFaq ? 'FAQ updated successfully' : 'FAQ added successfully');
        setShowModal(false);
        fetchFAQs();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to save FAQ');
      }
    } catch (error) {
      setError('Failed to save FAQ');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteFAQ = async (id: number) => {
    if (!confirm('Are you sure you want to delete this FAQ?')) return;

    try {
      const response = await fetch(getApiUrl(`/api/faqs/${id}`), {
        method: 'DELETE',
        credentials: 'include',
      });

      if (response.ok) {
        setSuccess('FAQ deleted successfully');
        fetchFAQs();
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (error) {
      setError('Failed to delete FAQ');
    }
  };

  if (loading) {
    return <div className="text-dark-text-secondary">Loading FAQs...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Success/Error Messages */}
      {success && (
        <div className="p-4 bg-green-900/20 border border-green-500 rounded-lg text-green-400">
          {success}
        </div>
      )}
      {error && (
        <div className="p-4 bg-red-900/20 border border-red-500 rounded-lg text-red-400">
          {error}
        </div>
      )}

      {/* Add FAQ Button */}
      <div className="flex justify-end">
        <button
          onClick={() => handleOpenModal()}
          className="btn btn-primary"
        >
          + Add New FAQ
        </button>
      </div>

      {/* FAQs List */}
      {faqs.length === 0 ? (
        <div className="card p-8 text-center">
          <p className="text-dark-text-secondary">
            No FAQs found. Add your first FAQ to get started.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {faqs.map((faq) => (
            <div key={faq.id} className="card p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-2">
                    <h3 className="text-base sm:text-lg font-bold text-white">{faq.question}</h3>
                    {faq.active === 0 && (
                      <span className="px-2 py-1 bg-red-900/20 border border-red-500 rounded text-xs text-red-400">
                        Inactive
                      </span>
                    )}
                  </div>
                  <p className="text-dark-text-secondary whitespace-pre-line mb-3">
                    {faq.answer}
                  </p>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs sm:text-sm text-dark-text-secondary">
                    <span>Page: {faq.page}</span>
                    {faq.category && <span>Category: {faq.category}</span>}
                    <span>Order: {faq.sort_order}</span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleOpenModal(faq)}
                    className="btn btn-secondary"
                    title="Edit FAQ"
                  >
                    ✏️
                  </button>
                  <button
                    onClick={() => handleDeleteFAQ(faq.id)}
                    className="btn btn-secondary text-red-400 hover:bg-red-900/20"
                    title="Delete FAQ"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit FAQ Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="card p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold text-white mb-4">
              {editingFaq ? 'Edit FAQ' : 'Add New FAQ'}
            </h3>
            <form onSubmit={handleSaveFAQ} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-dark-text mb-2">
                  Question *
                </label>
                <input
                  type="text"
                  value={formData.question}
                  onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                  className="input w-full"
                  placeholder="What is your question?"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-dark-text mb-2">
                  Answer *
                </label>
                <textarea
                  value={formData.answer}
                  onChange={(e) => setFormData({ ...formData, answer: e.target.value })}
                  className="input w-full min-h-[150px]"
                  placeholder="Provide a detailed answer..."
                  required
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-dark-text mb-2">
                    Page
                  </label>
                  <select
                    value={formData.page}
                    onChange={(e) => setFormData({ ...formData, page: e.target.value })}
                    className="input w-full"
                  >
                    <option value="homepage">Homepage</option>
                    <option value="faq">FAQ Page</option>
                    <option value="about">About</option>
                    <option value="contact">Contact</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-dark-text mb-2">
                    Category
                  </label>
                  <input
                    type="text"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="input w-full"
                    placeholder="e.g., general, pricing"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-dark-text mb-2">
                    Sort Order
                  </label>
                  <input
                    type="number"
                    value={formData.sort_order}
                    onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) })}
                    className="input w-full"
                    min="1"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-dark-text mb-2">
                    Status
                  </label>
                  <select
                    value={formData.active}
                    onChange={(e) => setFormData({ ...formData, active: parseInt(e.target.value) })}
                    className="input w-full"
                  >
                    <option value={1}>Active</option>
                    <option value={0}>Inactive</option>
                  </select>
                </div>
              </div>

              <div className="flex gap-3">
                <button type="submit" disabled={saving} className="btn btn-primary flex-1">
                  {saving ? 'Saving...' : editingFaq ? 'Update FAQ' : 'Add FAQ'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
